import { isDateInRange } from '@airship-platform/airship-patterns';
import type {
  AppointmentHistory,
  AppointmentHistoryFilter,
  ProposalStatusFilter,
} from './appointmentHistory.models';

export interface AppointmentWithTags extends AppointmentHistory {
  proposalStatus: ProposalStatusFilter[];
}

export function filterAppointments(
  appointments: AppointmentWithTags[],
  filter: AppointmentHistoryFilter
): AppointmentWithTags[] {
  const {
    status = [],
    startDateTime,
    endDateTime,
    proposalStatus = [],
    assignedUserIds = [],
    searchTerm = '',
    includeNonAirshipJobs,
  } = filter;

  return appointments.filter((appointment) => {
    if (startDateTime && endDateTime) {
      if (
        !isDateInRange(appointment.startDateTime, startDateTime, endDateTime)
      ) {
        return false;
      }
    }

    if (status.length > 0) {
      const hasMatchingStatus = status.some((s) => appointment.status === s);
      if (!hasMatchingStatus) {
        return false;
      }
    }

    if (proposalStatus.length > 0) {
      const hasMatchingTag = proposalStatus.some((tag) =>
        appointment.proposalStatus.includes(tag)
      );
      if (!hasMatchingTag) {
        return false;
      }
    }

    if (searchTerm) {
      const searchableText = [
        appointment.customerName,
        ...Object.values(appointment.customerAddress),
        appointment.technician?.firstName,
        appointment.technician?.lastName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!searchableText.includes(searchTerm.toLowerCase())) {
        return false;
      }
    }

    if (assignedUserIds.length > 0) {
      if (
        !appointment.technician?.userId ||
        !assignedUserIds.includes(appointment.technician.userId)
      ) {
        return false;
      }
    }

    return includeNonAirshipJobs ? true : !!appointment.technician;
  });
}

export function sortAppointments(
  appointments: AppointmentWithTags[],
  sortDirection: 'DATE_ASC' | 'DATE_DESC'
): AppointmentWithTags[] {
  return [...appointments].sort((a, b) => {
    const dateA = new Date(a.startDateTime).getTime();
    const dateB = new Date(b.startDateTime).getTime();

    return sortDirection === 'DATE_ASC' ? dateA - dateB : dateB - dateA;
  });
}

export function appendProposalStatus(
  appointments: AppointmentHistory[]
): AppointmentWithTags[] {
  return appointments.map((appointment) => {
    const proposalStatus: ProposalStatusFilter[] = [];

    if (appointment.proposals.length > 0) {
      proposalStatus.push('PROPOSAL_CREATED');

      if (!appointment.proposals.every((p) => p.hasBeenShared)) {
        proposalStatus.push('PROPOSAL_NOT_SHARED');
      } else {
        proposalStatus.push('PROPOSAL_SHARED');
      }

      if (appointment.proposals.some((p) => p.hasSoldOption)) {
        proposalStatus.push('PROPOSAL_ACCEPTED');
      }
    }

    return {
      ...appointment,
      proposalStatus,
    };
  });
}
